import { createContext, useState, useContext, useMemo, useEffect } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { useFetchTeams } from '../hooks/useFetchUsersAndTeams';
import usePermissionsLimitData from '../hooks/usePermissionsLimitData';
import { RequestStatuses, useContentObjectType } from 'content-management-data-lib';
import { useObjectTypeHasCampaignsAccess } from 'campaigns-lib/campaignsPlatform';

/*
 * PermissionsExtraDataProvider is a provider that fetches and provides extra data
 * that is needed in display/enable permissions checks for Content Platform `ContentAction`s.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const PERMISSIONS_EXTRA_DATA_DEFAULT_STATE = {
  teams: [],
  teamsRequestStatus: RequestStatuses.UNINITIALIZED,
  limitData: {
    hasExceededContentGroupLimit: false,
    hasExceededContentObjectLimit: false,
    hasReachedContentObjectLimit: false,
    multiLangGroupSizeLimit: null
  },
  objectTypeHasCampaignsAccess: false
};
export const PermissionsExtraDataContext = /*#__PURE__*/createContext(undefined);
export const PermissionsExtraDataProvider = ({
  children
}) => {
  const [permissionsExtraDataState, setPermissionsExtraDataState] = useState(PERMISSIONS_EXTRA_DATA_DEFAULT_STATE);
  const contextValue = useMemo(() => {
    return permissionsExtraDataState;
  }, [permissionsExtraDataState]);
  const {
    teams,
    teamsRequestStatus
  } = useFetchTeams();
  const prevTeamsRequestStatus = usePrevious(teamsRequestStatus);
  const {
    limitData,
    contentUsageLimitRequestsSucceeded
  } = usePermissionsLimitData();
  const {
    contentObjectType
  } = useContentObjectType();
  const objectTypeHasCampaignsAccess = useObjectTypeHasCampaignsAccess(contentObjectType);
  useEffect(() => {
    setPermissionsExtraDataState(prevState => Object.assign({}, prevState, {
      objectTypeHasCampaignsAccess
    }));
  }, [objectTypeHasCampaignsAccess]);
  useEffect(() => {
    if (contentUsageLimitRequestsSucceeded) {
      setPermissionsExtraDataState(prevState => Object.assign({}, prevState, {
        limitData
      }));
    }
  }, [limitData, contentUsageLimitRequestsSucceeded]);
  useEffect(() => {
    if (teamsRequestStatus === RequestStatuses.SUCCEEDED && prevTeamsRequestStatus !== RequestStatuses.SUCCEEDED) {
      setPermissionsExtraDataState(Object.assign({}, permissionsExtraDataState, {
        teams,
        teamsRequestStatus: RequestStatuses.SUCCEEDED
      }));
    }
  }, [permissionsExtraDataState, prevTeamsRequestStatus, teams, teamsRequestStatus]);
  return /*#__PURE__*/_jsx(PermissionsExtraDataContext.Provider, {
    value: contextValue,
    children: children
  });
};
export const usePermissionsExtraData = () => {
  const context = useContext(PermissionsExtraDataContext);
  if (context === undefined) {
    throw new Error('usePermissionsExtraData must be used within a PermissionsExtraDataContext');
  }
  return context;
};